import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Stack,
  Typography,
} from "@mui/material";
import {
  Control,
  Path,
  PathValue,
  UseFormSetValue,
  UseFormTrigger,
} from "react-hook-form";
import RequiredLabel from "@/components/common/forms/requiredLabel";
import { ChevronSmallDownIcon } from "@/components/common/icons";
import { PRIMARY } from "@/config/mui/colorPalette";
import { UpdateClientMutationVariables } from "@/graphql/mutations/clients.graphql.types";
import { CreateClientInputs } from "../../../../views/serviceFlow/clients/createClientView";
import { formSections } from "./clientFormHelpers";
import { FormTextField } from "./formFields";

type FormSchemaType = CreateClientInputs | UpdateClientMutationVariables;

function ClientForm<FormSchema extends FormSchemaType>({
  control,
  setValue,
  trigger,
}: {
  control: Control<FormSchema>;
  setValue: UseFormSetValue<FormSchema>;
  trigger: UseFormTrigger<FormSchema>;
}) {
  return (
    <form style={{ width: "100%" }}>
      <Stack spacing={2}>
        <Typography
          variant="subtitleSmall"
          sx={{
            color: "secondary.main",
          }}
        >
          Personal info
        </Typography>
        {formSections.generalClientData.map(
          ({ name, label, errorText, component }) => {
            const FormElement = component;

            return (
              <FormElement<FormSchema>
                key={name}
                control={control}
                name={name as Path<FormSchema>}
                label={<RequiredLabel label={label} />}
                trigger={trigger}
                sx={textFieldSx}
                size="small"
                helperText={errorText ? errorText : "Field is required"}
                inputContainerSx={phoneInputContainerSx}
              />
            );
          }
        )}
      </Stack>

      <Stack spacing={2}>
        <Accordion sx={{ width: "100%", boxShadow: "none" }} defaultExpanded>
          <AccordionSummary
            expandIcon={<ChevronSmallDownIcon color={PRIMARY} />}
            aria-controls="additional-details-content"
            id="additional-details-header"
            sx={accordionSummarySx}
          >
            <Typography
              variant="subtitleSmall"
              sx={{
                color: "secondary.main",
              }}
            >
              Additional details (optional)
            </Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ padding: 0 }}>
            <Stack spacing={2}>
              {formSections.additionalDetails.map(
                ({ name, label, component, choices }) => {
                  const FormElement = component;

                  return (
                    <FormElement<FormSchema>
                      key={name}
                      control={control}
                      name={name as Path<FormSchema>}
                      label={label}
                      setValue={setValue}
                      choices={choices}
                      trigger={trigger}
                      sx={detailsSx}
                      labelVariant="subtitleSmall"
                      radioGroupSx={{
                        display: "flex",
                        flexDirection: "row",
                        gap: 2,
                      }}
                    />
                  );
                }
              )}
            </Stack>
          </AccordionDetails>
        </Accordion>
      </Stack>

      <Stack spacing={2}>
        <Accordion sx={{ width: "100%", boxShadow: "none" }} defaultExpanded>
          <AccordionSummary
            expandIcon={<ChevronSmallDownIcon color={PRIMARY} />}
            aria-controls="about-content"
            id="about-header"
            sx={accordionSummarySx}
          >
            <Typography
              variant="subtitleSmall"
              sx={{
                color: "secondary.main",
              }}
            >
              About (optional)
            </Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ padding: 0 }}>
            <FormTextField<FormSchema>
              control={control}
              name={"note" as Path<FormSchema>}
              label="Note"
              multiline
              minRows={4}
              slotProps={{
                htmlInput: {
                  maxLength: 300,
                },
              }}
              defaultValue={"" as PathValue<FormSchema, Path<FormSchema>>}
              size="small"
            />
          </AccordionDetails>
        </Accordion>
      </Stack>
      <Stack spacing={2}>
        <Accordion
          sx={{ width: "100%", boxShadow: "none", pb: 4 }}
          defaultExpanded
        >
          <AccordionSummary
            expandIcon={<ChevronSmallDownIcon color={PRIMARY} />}
            aria-controls="address-content"
            id="address-header"
            sx={accordionSummarySx}
          >
            <Typography
              variant="subtitleSmall"
              sx={{
                color: "secondary.main",
              }}
            >
              Address (optional)
            </Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ padding: 0 }}>
            <Stack spacing={2}>
              {formSections.address.map(({ name, label, component }) => {
                const FormElement = component;

                return (
                  <FormElement<FormSchema>
                    key={name}
                    control={control}
                    name={name as Path<FormSchema>}
                    label={label}
                    setValue={setValue}
                    trigger={trigger}
                    size="small"
                  />
                );
              })}
            </Stack>
          </AccordionDetails>
        </Accordion>
      </Stack>
    </form>
  );
}

export default ClientForm;

const textFieldSx = {
  "& .MuiInputBase-input": {
    padding: "10px 14px",
  },
  "& .PhoneInput input": {
    height: "38px",
  },
  "& .PhoneInput": {
    height: "38px",
  },
  "& .PhoneInput--focus input": {
    height: "38px",
  },
  "& .PhoneNumberLabelEmpty": {
    top: "-8px",
  },
};

const phoneInputContainerSx = {
  height: "40px",
};

const detailsSx = {
  "& .MuiOutlinedInput-root": {
    height: "40px",
  },
  "& .MuiInputLabel-root:not(.MuiInputLabel-shrink)": {
    top: "-7px",
  },
  "& .MuiInputLabel-root.MuiInputLabel-shrink": {
    top: "0px",
  },
  "& .MuiFormControlLabel-root": {
    marginLeft: 0,
  },
};

const accordionSummarySx = {
  px: 0,
  pt: 1,
  minHeight: "48px !important",
  "& .MuiAccordionSummary-content": {
    margin: "12px 0 !important",
  },
};
